import { useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { EmptyState, ProgressBar } from "@/components/new-look/blocks";
import { countOf, type Stage } from "@/components/new-look/labels";
import { NEEDS_YOU_TEXT, stageOf, type IdeaStageResult } from "@/components/new-look/stage";
import { useEpics } from "@/hooks/queries/use-epics";
import { usePageTitle } from "@/hooks/use-page-title";
import type { Epic } from "@/lib/api/types";
import { cn } from "@/lib/utils";

/**
 * Blueprints (docs/new-look/PLAN.md, step 5.1): every blueprint (epic) with
 * where it is (ready to build, building, done), what needs you, its parts and
 * progress. Tabs filter the loaded blueprints; the list loads 100 at a time.
 */

const PAGE_SIZE = 100;

/** A blueprint's stage, from its build facts, with the shared idea rules. */
export function blueprintStage(epic: Epic): IdeaStageResult {
  const f = epic.facts;
  return stageOf({
    request: { status: "converted", convertedEpicId: epic.id },
    epic: {
      state: epic.state ?? "active",
      displayState: f?.epic?.displayState ?? null,
      completedFeatureCount: f?.epic?.completedFeatureCount ?? epic.completedFeatureCount ?? 0,
      featureCount: f?.epic?.featureCount ?? epic._count?.features ?? null,
    },
    planJob: f?.planJob ?? null,
    buildJob: f?.buildJob ?? null,
    openPullRequests: f?.openPullRequests ?? 0,
  });
}

export const BLUEPRINT_STATUS: Record<Stage, { label: string; chip: string }> = {
  idea: {
    label: "Ready to build",
    chip: "bg-cyan-100 text-cyan-800 dark:bg-cyan-500/15 dark:text-cyan-200",
  },
  blueprint: {
    label: "Ready to build",
    chip: "bg-cyan-100 text-cyan-800 dark:bg-cyan-500/15 dark:text-cyan-200",
  },
  build: {
    label: "Building",
    chip: "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-200",
  },
  done: {
    label: "Done",
    chip: "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-200",
  },
};

type TabKey = "all" | "needs" | "ready" | "building" | "done";

const TABS: { key: TabKey; label: string; test: (r: IdeaStageResult) => boolean }[] = [
  { key: "all", label: "All", test: () => true },
  { key: "needs", label: "Waiting for you", test: (r) => r.needsYou !== null },
  {
    key: "ready",
    label: "Ready to build",
    test: (r) => r.stage === "blueprint" || r.stage === "idea",
  },
  { key: "building", label: "Building", test: (r) => r.stage === "build" },
  { key: "done", label: "Done", test: (r) => r.stage === "done" },
];

const GRID =
  "grid grid-cols-[minmax(0,1fr)_auto] gap-3 md:grid-cols-[minmax(0,2.6fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,0.9fr)]";

function ago(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : formatDistanceToNow(d, { addSuffix: true });
}

function BlueprintRow({ epic, result }: { epic: Epic; result: IdeaStageResult }) {
  const status = BLUEPRINT_STATUS[result.stage];
  const parts = epic.facts?.epic?.featureCount ?? epic._count?.features ?? 0;
  const done = epic.facts?.epic?.completedFeatureCount ?? epic.completedFeatureCount ?? 0;
  const progress =
    result.stage === "done" ? 100 : parts > 0 ? Math.round((done / parts) * 100) : null;
  return (
    <li>
      <Link
        to={`/blueprints/${epic.id}`}
        className={cn(
          GRID,
          "items-center border-b border-border px-4 py-3.5 transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none md:px-5"
        )}
      >
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-2">
            <span className="truncate text-[15px] font-semibold text-foreground">{epic.name}</span>
            {result.needsYou && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900 dark:bg-amber-500/15 dark:text-amber-200">
                {NEEDS_YOU_TEXT[result.needsYou]}
              </span>
            )}
          </span>
          <span className="truncate text-[13px] text-muted-foreground">
            {[epic.identifier, epic.creator?.name].filter(Boolean).join(" · ")}
          </span>
        </span>
        <span className="hidden truncate text-sm text-foreground/80 md:block">
          {epic.team?.name ?? "Personal"}
        </span>
        <span
          className={cn(
            "inline-flex min-h-6 items-center self-center justify-self-start rounded-full px-2.5 text-xs font-semibold",
            status.chip
          )}
        >
          {status.label}
        </span>
        <span className="hidden text-sm tabular-nums text-foreground/80 md:block">
          {parts > 0 ? `${String(done)} of ${String(parts)}` : "—"}
        </span>
        {progress === null ? (
          <span className="hidden text-sm text-muted-foreground md:block">—</span>
        ) : (
          <span className="hidden items-center gap-2 md:flex">
            <ProgressBar
              value={progress}
              label={`${epic.name} progress`}
              stage={result.stage}
              className="flex-1"
            />
            <span className="w-9 text-right text-xs tabular-nums text-muted-foreground">
              {progress}%
            </span>
          </span>
        )}
        <span className="hidden text-[13px] text-muted-foreground md:block">
          {ago(epic.updatedAt)}
        </span>
      </Link>
    </li>
  );
}

export function AllBlueprintsPage() {
  usePageTitle("Blueprints");
  const [tab, setTab] = useState<TabKey>("all");
  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } = useEpics({
    limit: PAGE_SIZE,
    withFacts: true,
    orderBy: "updatedAt",
    orderDirection: "desc",
  });
  const epics = data?.pages.flatMap((p) => p.data) ?? [];
  const scored = epics.map((epic) => ({ epic, result: blueprintStage(epic) }));
  const counts = Object.fromEntries(
    TABS.map((t) => [t.key, scored.filter((s) => t.test(s.result)).length])
  ) as Record<TabKey, number>;
  const current = TABS.find((t) => t.key === tab) ?? TABS[0];
  const shown = scored.filter((s) => current?.test(s.result));

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">Blueprints</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          The plan for each idea: its parts, tasks and checks. Start one building when it's ready.
          {epics.length > 0 &&
            ` ${countOf(epics.length, "blueprint")}${hasNextPage ? " loaded" : ""}.`}
        </p>
      </div>

      <nav
        aria-label="Views"
        className="-mx-4 flex gap-1 overflow-x-auto border-b border-border px-4 sm:mx-0 sm:px-0"
      >
        {TABS.map((t) => {
          const on = t.key === tab;
          return (
            <button
              key={t.key}
              type="button"
              aria-current={on ? "page" : undefined}
              onClick={() => {
                setTab(t.key);
              }}
              className={cn(
                "-mb-px inline-flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                on
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {t.label}
              {!isLoading && (
                <span
                  className={cn(
                    "rounded-full px-1.5 py-px text-xs tabular-nums",
                    on ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
                    t.key === "needs" &&
                      counts.needs > 0 &&
                      !on &&
                      "bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200"
                  )}
                >
                  {counts[t.key]}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {isLoading ? (
        <div aria-busy="true" aria-label="Loading blueprints" className="flex flex-col gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          title="Couldn't load blueprints"
          body="Check your connection and refresh the page."
        />
      ) : shown.length === 0 ? (
        <EmptyState
          title={epics.length === 0 ? "No blueprints yet" : "Nothing here"}
          body={
            epics.length === 0
              ? "A blueprint is made from an idea once it's clear. Start with an idea."
              : "No blueprints are in this state right now."
          }
          action={
            epics.length === 0 ? (
              <Link
                to="/ideas"
                className="inline-flex min-h-11 items-center rounded-lg border border-border px-4 text-sm font-medium hover:bg-muted"
              >
                Go to Ideas
              </Link>
            ) : undefined
          }
        />
      ) : (
        <section
          aria-label="Blueprints"
          className="overflow-hidden rounded-2xl border border-border"
        >
          <div
            aria-hidden="true"
            className={cn(
              GRID,
              "hidden border-b border-border bg-muted/50 px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground md:grid"
            )}
          >
            <span>Blueprint</span>
            <span>Project</span>
            <span>Status</span>
            <span>Parts</span>
            <span>Progress</span>
            <span>Updated</span>
          </div>
          <ul>
            {shown.map(({ epic, result }) => (
              <BlueprintRow key={epic.id} epic={epic} result={result} />
            ))}
          </ul>
        </section>
      )}

      {hasNextPage && (
        <button
          type="button"
          disabled={isFetchingNextPage}
          onClick={() => {
            void fetchNextPage();
          }}
          className="min-h-11 self-center rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-60"
        >
          {isFetchingNextPage ? "Loading…" : "Show more"}
        </button>
      )}
    </div>
  );
}
